import { filesAPIService } from '~/api/files';
import { appMicrositeAPIService } from '~/api/microsites/app';
import { imagesMicrositeAPIService } from '~/api/microsites/images';
import { listOfLinksMicrositeAPIService } from '~/api/microsites/links';
import { pdfMicrositeAPIService } from '~/api/microsites/pdf';
import { socialMediaMicrositeAPIService } from '~/api/microsites/social-media';
import { vcardMicrositeAPIService } from '~/api/microsites/vcard-plus';
import { convertMicrositeTypeToId, getFileFromLocalSource } from '~/utils/common';

import { getStatsFromQrCodeData, updateImagesData, uploadLinksDataWithImages } from './utils';

export const modelMicrositeService = {
  listOfLinks: async (initialModel, qrCodeState, redux, isCreate, micrositeData) => {
    const {
      bacgkroundColor,
      basicInformation: { h1, h2 },
      selectedTemplate,
      fontsSize,
      fonts,
      content: { linkList, socialLinks },
      basicInformation: {
        coverImage,
        logoImage: { url, scale, borderRadius, rotate },
      },
    } = initialModel;

    const {
      data: { id },
    } = qrCodeState;

    const logoImageFile = await getFileFromLocalSource(`${window.location.origin}${url}`);
    const coverImageFile = await getFileFromLocalSource(`${window.location.origin}${coverImage.url}`);

    const logo_id = await filesAPIService.uploadImage(logoImageFile);
    const cover_image_id = await filesAPIService.uploadImage(coverImageFile);

    const links = await uploadLinksDataWithImages(linkList, redux);
    const socials = socialLinks.map((item) => {
      return { network: item.label.toUpperCase(), url: item.value };
    });
    const requestBody = {
      qr_code_id: id,
      selected_template: selectedTemplate,
      design: {
        primary: bacgkroundColor[0],
        secondary: bacgkroundColor[1],
        tertiary: bacgkroundColor[2],
      },
      fonts: {
        ...fonts,
        ...fontsSize,
      },
      title: h1,
      description: h2,
      socials,
      logo: {
        scale,
        border_radius: borderRadius,
        rotate,
        logo_id: logo_id?.data,
      },
      links,
      cover_image_id: cover_image_id?.data,
      images_view_type: 'VERTICAL',
    };

    if (isCreate) {
      const res = await listOfLinksMicrositeAPIService.createListOfLinksSite(requestBody);
      const responseLinks = res.data?.links;
      const currentLinksArr = [...linkList];

      responseLinks.forEach((item, index) => {
        currentLinksArr[index] = { ...currentLinksArr[index], unique_id: item.id };
      });

      redux.dispatch(redux.actions.setMicrositeData(res.data));
      redux.dispatch(redux.actions.setContentLinkList(currentLinksArr));
      redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));

      return;
    }

    listOfLinksMicrositeAPIService.updateListOfLinksSite({ id: micrositeData.id, body: requestBody });
  },

  pdf: async (initialModel, qrCodeState, redux, isCreate, micrositeData) => {
    const {
      data: { id },
    } = qrCodeState;

    const {
      bacgkroundColor,
      selectedTemplate,
      fonts: { head, text },
      basicInformation: { coverImage, images },

      pdfInformation: { company, title, description, website, button },
    } = initialModel;

    const coverImageFile = await getFileFromLocalSource(`${window.location.origin}${coverImage?.img}`);
    const cover_image_id = await filesAPIService.uploadImage(coverImageFile);

    const uploadPromises = images.map(async (item, index) => {
      const file = await getFileFromLocalSource(`${window.location.origin}${item.previewPdfLink}`);
      const image = await getFileFromLocalSource(`${window.location.origin}${item.previewLink}`);

      const storagePdfResponse = await filesAPIService.uploadImage(file);
      const storageImageResponse = await filesAPIService.uploadImage(image);

      return {
        file_id: storagePdfResponse.data,
        preview_image_id: storageImageResponse.data,
        name: item.path,
        description: item.description,
        size: item.size,
        position: index,
      };
    });

    const pdfs = await Promise.all(uploadPromises);

    const requestBody = {
      qr_code_id: id,
      selected_template: selectedTemplate,
      design: {
        primary: bacgkroundColor[0],
        secondary: bacgkroundColor[1],
      },
      fonts: {
        title_font: head,
        text_font: text,
      },
      title: title,
      company: company,
      description: description,
      website: website,
      button: button,
      cover_image_id: cover_image_id?.data,
      pdfs: pdfs,
    };

    if (isCreate) {
      const res = await pdfMicrositeAPIService.createPdfSite(requestBody);
      const responsePdfs = res.data?.pdfs;
      const currentPdfsArr = [...images];

      responsePdfs.forEach((item, index) => {
        currentPdfsArr[index] = { ...currentPdfsArr[index], unique_id: item.id };
      });

      redux.dispatch(redux.actions.setMicrositeData(res.data));
      redux.dispatch(redux.actions.setImages(currentPdfsArr));
      redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));

      return;
    }

    const resp = await pdfMicrositeAPIService.updatePdfSite(requestBody, micrositeData.id);
    const responsePdfs = resp.data?.pdfs;
    const currentPdfsArr = [...images];

    responsePdfs.forEach((item, index) => {
      currentPdfsArr[index] = { ...currentPdfsArr[index], unique_id: item.id };
    });

    redux.dispatch(redux.actions.setMicrositeData(resp.data));
    redux.dispatch(redux.actions.setImages(currentPdfsArr));

    return resp;
  },

  images: async (initialModel, qrCodeState, redux) => {
    const {
      bacgkroundColor,
      fonts,
      images,
      imageInformation: { header, description, website },
      buttons,
    } = initialModel;
    const {
      data: { id, micrositeType },
    } = qrCodeState;

    const imagesList = await updateImagesData(images, redux, convertMicrositeTypeToId(micrositeType), true);

    const requestBody = {
      qr_code_id: id,
      template_type: 0,
      design: {
        primary: bacgkroundColor[0],
      },
      fonts: fonts,
      title: header,
      description,
      website,
      images: imagesList,
      buttons: buttons.map((item) => {
        return { text: item.text, url: item.url, position: 0 };
      }),
    };

    const res = await imagesMicrositeAPIService.createImagesSite(requestBody);

    redux.dispatch(redux.actions.setImages(res?.data?.images));
    redux.dispatch(redux.actions.setMicrositeData(res.data));
    redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));
  },
  app: async (initialModel, qrCodeState, redux) => {
    const {
      logo,
      platformLinks,
      bacgkroundColor,
      appInformation: { appName, developerCompany, description, website },
    } = initialModel;

    const {
      data: { id },
    } = qrCodeState;

    const requestBody = {
      qr_code_id: id,
      design: {
        primary: bacgkroundColor[0],
      },
      fonts: {},
      name: appName,
      developer: developerCompany,
      description,
      website,
      logo_id: logo?.imageStorageId,
      app: {
        android: platformLinks.google,
        iphone: platformLinks.apple,
      },
    };

    const res = await appMicrositeAPIService.createAppSite(requestBody);

    redux.dispatch(redux.actions.setMicrositeData(res.data));
    redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));
  },

  socialMedia: async (initialModel, qrCodeState, redux) => {
    const {
      bacgkroundColor,
      socialLinks,
      appInformation: { head, description },
    } = initialModel;

    const {
      data: { id },
    } = qrCodeState;

    const socials = socialLinks.map((item) => {
      return { network: item.network.toUpperCase(), url: item.value, title: item.title };
    });

    const requestBody = {
      qr_code_id: id,
      design: {
        primary: bacgkroundColor[0],
      },
      fonts: {},
      title: head,
      description: description,
      socials,
    };

    const res = await socialMediaMicrositeAPIService.createSocialMediaSite(requestBody);
    redux.dispatch(redux.actions.setMicrositeData(res.data));
    redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));
  },

  vcardPlus: async (initialModel, qrCodeState, redux, isCreate, qrId) => {
    const {
      selectedTemplate,
      bacgkroundColor,
      basicInformation: {
        name,
        surName,
        titleValue,
        titlePosition,
        logoImage,
        coverImage,
        summary,
        contactInfo,
        location,
      },
      content: {
        socialTitle,
        imagesType,
        footerTitle,
        headerPhone,
        headerLocation,
        headerEmail,
        socialLinks,
        companies,
      },
    } = initialModel;

    const {
      data: { id },
    } = qrCodeState;

    const correctId = qrId ? qrId : id;

    const titlePositionBE = titlePosition === 'center' ? 'CENTERED' : titlePosition.toUpperCase();
    const logoImageFile = await getFileFromLocalSource(`${window.location.origin}${logoImage.url}`);
    const coverImageFile = await getFileFromLocalSource(`${window.location.origin}${coverImage.url}`);

    const logo_id = await filesAPIService.uploadImage(logoImageFile);
    const cover_image_id = await filesAPIService.uploadImage(coverImageFile);

    const socials = socialLinks.map((item) => {
      return { network: item.label.toUpperCase(), url: item.value, title: item.title };
    });

    const requestBody = {
      qr_code_id: correctId,
      selected_template: selectedTemplate,
      design: {
        primary: bacgkroundColor[0],
        secondary: bacgkroundColor[1],
      },
      fonts: {},
      person: {
        first_name: name,
        last_name: surName,
        title: titleValue,
        photo: { ...logoImage, logo_id: logo_id.data },
      },
      cover_image_id: cover_image_id.data,
      person_title: titlePositionBE,
      summary: summary,
      images_view_type: imagesType,
      socials_title: socialTitle,
      socials,
      contact_storage_option: 'CONTACT_STORAGE_TITLE_ONLY', //todo maybe delete
      header: {
        header_phone: headerPhone,
        header_location: headerLocation,
        header_email: headerEmail,
      },
      footer: footerTitle,
      contact_add_btn_on_the_top: false,
      contact_add_btn_floating_button: false,
    };

    const res = await vcardMicrositeAPIService.createVCardSite(requestBody);
    if (res.data) {
      // const { url, ...payloadPhoto } = logoImage;
      // const personBody = {
      //   first_name: name,
      //   last_name: surName,
      //   title: titleValue,
      //   photo: { ...payloadPhoto, logo_id: logo_id.data },
      // };
      // await vcardMicrositeAPIService.updateVCardPerson(personBody, res.data.person.id);

      // companies
      const uploaderCompany = companies.map(async (item, index) => {
        const { id, ...newCompanies } = item;
        const companyBody = { ...newCompanies, microsite: res.data.id, position: index };
        return await vcardMicrositeAPIService.createVCardCompany(companyBody);
      });
      const respCompanies = await Promise.all(uploaderCompany);
      const currentCompanyArr = [...companies];

      respCompanies.forEach((item, index) => {
        currentCompanyArr[index] = { ...currentCompanyArr[index], unique_id: item.data.id };
      });
      redux.dispatch(redux.actions.setCompanies(currentCompanyArr));

      //phones
      const uploaderPhones = contactInfo.phone.map(async (item) => {
        const payload = {
          type: item.type === 'mobile' ? 'CELL' : item.type.toUpperCase(),
          title: item.label,
          phone: item.value,
          microsite: res.data.id,
        };
        return await vcardMicrositeAPIService.createVCardPhones(payload);
      });
      await Promise.all(uploaderPhones);

      //emails
      const uploaderEmail = contactInfo.email.map(async (item) => {
        const payload = {
          title: item.label,
          email: item.value,
          microsite: res.data.id,
        };
        return await vcardMicrositeAPIService.createVCardEmails(payload);
      });
      await Promise.all(uploaderEmail);

      //website
      const uploaderWebsite = contactInfo.website.map(async (item) => {
        const payload = {
          title: item.label,
          url: item.value,
          microsite: res.data.id,
        };
        return await vcardMicrositeAPIService.createVCardWebsite(payload);
      });
      await Promise.all(uploaderWebsite);

      await vcardMicrositeAPIService.updateVCardLocation(location, res.data.location.id);
    }
    const updatedResponse = await vcardMicrositeAPIService.getVCardSite(res.data.id);

    redux.dispatch(redux.actions.setMicrositeData(updatedResponse.data));
    redux.dispatch(redux.actions.setStatsData(getStatsFromQrCodeData(qrCodeState.data)));

    // if (isCreate) {
    // const res = await vcardMicrositeAPIService.createVCardSite(requestBody);
    // const responseLinks = res.data?.links;
    // let currentLinksArr = [...linkList];

    // responseLinks.forEach((item, index) => {
    //   currentLinksArr[index] = { ...currentLinksArr[index], unique_id: item.id };
    // });

    // redux.dispatch(redux.actions.setContentLinkList(currentLinksArr));

    //   return;
    // }

    // vcardMicrositeAPIService.replaceVCardSite({ id: micrositeData.id, body: requestBody });
  },
};
